import { confirmEmailChange, isValidEmail, requestEmailChange, type EmailChange, type User } from "../lib/auth.js";
import { el } from "../lib/dom.js";
import { actionRow, messageOf, field, type Popup, type PopupSpec } from "./ui.js";

interface Host {
  openPopup(spec: PopupSpec): Popup;
  announce(message: string): void;
  updateUser(user: User): void;
}

export function openEmailChange(host: Host, user: User, returnFocusTo: HTMLElement | (() => HTMLElement | undefined)) {
  let pending: EmailChange | undefined;
  let busy = false;
  let disposed = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const email = el("input", { attrs: { type: "email", autocomplete: "email", maxlength: 254, required: true } });
  const code = el("input", { attrs: { type: "text", inputmode: "numeric", autocomplete: "one-time-code", maxlength: 64 } });
  const status = el("p", { class: "popup-note email-change-status" });
  const help = el("p", { class: "popup-note", text: "Verify your new address to change where you sign in. Other browsers will be signed out. This browser and agent connections will stay signed in." });
  const inputSlot = el("div", { children: [field("change-email-address", "New email address", email)] });
  const submit = el("button", { text: "Send code", attrs: { type: "submit" } });
  const resend = el("button", { class: "secondary", text: "Send again", attrs: { type: "button", hidden: true },
    on: { click: () => { if (!busy && pending) void sendCode(pending.email); } } });
  const edit = el("button", { class: "secondary", text: "Use another address", attrs: { type: "button", hidden: true }, on: { click: () => {
    if (busy) return;
    pending = undefined; clearTimeout(timer); code.value = "";
    inputSlot.replaceChildren(field("change-email-address", "New email address", email));
    submit.textContent = "Send code"; resend.hidden = true; edit.hidden = true; status.textContent = "";
    submit.disabled = false; email.focus();
  } } });
  const cancel = el("button", { class: "secondary", text: "Cancel", attrs: { type: "button" }, on: { click: () => popup.dismiss() } });
  const form = el("form", { class: "email-change-form", children: [help, inputSlot, status,
    actionRow([cancel], submit),
    actionRow([resend, edit])], on: { submit: event => {
    event.preventDefault(); if (busy || disposed) return;
    if (!pending) {
      if (!isValidEmail(email.value) || email.value.trim().toLowerCase() === user.email.toLowerCase()) {
        say("Enter a different, valid email address."); email.focus(); return;
      }
      void sendCode(email.value);
    } else { void confirm(); }
  } } });
  function say(message: string) { status.textContent = message; host.announce(message); }
  function setBusy(value: boolean) {
    busy = value;
    for (const control of [email, code, submit, resend, edit, cancel]) control.disabled = value;
    form.setAttribute("aria-busy", String(value));
  }
  function expire() {
    if (disposed || busy || !pending || pending.expiresAt > Date.now()) return;
    submit.disabled = true; say("This code has expired. Send again to request a new one.");
  }
  async function sendCode(address: string) {
    setBusy(true);
    try {
      const result = await requestEmailChange(address);
      if (disposed) return;
      pending = result; email.value = result.email;
      code.value = ""; code.removeAttribute("aria-invalid");
      inputSlot.replaceChildren(field("change-email-code", "Verification code", code));
      submit.textContent = "Change email"; resend.hidden = false; edit.hidden = false;
      say(`If ${result.email} can be used, a code is on its way. Use the latest code received; it expires 10 minutes after it was issued.`);
      clearTimeout(timer); timer = setTimeout(expire, Math.max(0, result.expiresAt - Date.now()));
    } catch (error) { if (!disposed) say(messageOf(error)); }
    finally { if (!disposed) { setBusy(false); expire(); (pending ? code : email).focus(); } }
  }
  async function confirm() {
    if (!pending) return;
    if (pending.expiresAt <= Date.now()) { expire(); return; }
    if (!/^\d{6}$/.test(code.value.replace(/\s/g, ""))) { say("Enter the six-digit code from your email."); code.focus(); return; }
    setBusy(true);
    try {
      const updated = await confirmEmailChange(pending, code.value);
      if (disposed) return;
      host.updateUser(updated);
      setBusy(false); popup.close(); host.announce("Email address changed. Other browsers have been signed out.");
    } catch (error) { if (!disposed) { say(messageOf(error)); code.setAttribute("aria-invalid", "true"); } }
    finally { if (!disposed) { setBusy(false); expire(); code.focus(); } }
  }
  code.addEventListener("input", () => code.removeAttribute("aria-invalid"));
  window.addEventListener("focus", expire);
  document.addEventListener("visibilitychange", expire);
  const popup = host.openPopup({ title: "Change email", body: form, focus: email, returnFocusTo,
    busy: () => busy, guard: { dirty: () => email.value.length > 0 || code.value.length > 0,
      title: "Cancel this address change?", message: "Your current address stays unchanged unless confirmation has already reached the server.", keep: "Keep editing", discard: "Cancel change" },
    onClose: () => { disposed = true; clearTimeout(timer); window.removeEventListener("focus", expire); document.removeEventListener("visibilitychange", expire); },
  });
  return popup;
}
